import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";

const GRID_LEVELS = 10;

const LONG_DEFAULTS = {
  min_change_pct_long: "5",
  max_funding_pct_long: "0.01",
  entry_usdt_long: "10",
  leverage_info_long: "5",
  benefit_ratio_long: "3",
  benefit_ratio_after_grid_long: "20",
  max_symbols_long: "10",
  ha_tf_long: "",
};

const HA_TF_OPTIONS = [
  { value: "", label: "사용 안 함" },
  { value: "30m", label: "30분" },
  { value: "1h", label: "1시간" },
  { value: "4h", label: "4시간" },
];

const HA_TF_LONG_OPTIONS = HA_TF_OPTIONS;

const HA_BREAK_TF_OPTIONS = [
  { value: "30m", label: "30분" },
  { value: "1h", label: "1시간" },
  { value: "4h", label: "4시간" },
  { value: "1d", label: "1일" },
  { value: "1w", label: "1주" },
];

const SHORT_DEFAULTS = {
  min_change_pct: "5",
  min_funding_pct: "0.01",
  entry_usdt: "10",
  leverage_info: "5",
  benefit_ratio: "3",
  benefit_ratio_after_grid: "20",
  max_symbols: "10",
  ha_break_short: "0",
  ha_break_tf_short: "1h",
  ha_break_bars_short: "20",
  crypto_futures_only: "0",
  short_trade_mode: "auto",
};

const EMPTY_GRIDS = () =>
  Array.from({ length: GRID_LEVELS }, () => ({ usdt: "0", rate: "0" }));

const MEME_PRESET_MAX = 12;
const memePresetStorageKey = (targetUid, side) => `meme_presets_${side}_v1_${targetUid}`;

function readMemePresets(targetUid, side) {
  if (!targetUid) return [];
  try {
    const raw = localStorage.getItem(memePresetStorageKey(targetUid, side));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeMemePresets(targetUid, side, list) {
  if (!targetUid) return;
  localStorage.setItem(memePresetStorageKey(targetUid, side), JSON.stringify(list));
}

function buildMemePresetSnapshot(form, grids) {
  return {
    form: { ...form },
    grids: grids.map((row) => ({ ...row })),
  };
}

function applyMemePresetSnapshot(snapshot, defaults, setForm, setGrids) {
  if (!snapshot || typeof snapshot !== "object") return;
  if (snapshot.form) {
    setForm({ ...defaults, ...snapshot.form });
  }
  if (Array.isArray(snapshot.grids)) {
    setGrids(
      Array.from({ length: GRID_LEVELS }, (_, i) => ({
        usdt: String(snapshot.grids[i]?.usdt ?? "0"),
        rate: String(snapshot.grids[i]?.rate ?? "0"),
      }))
    );
  }
}

function shortFormFromRow(row) {
  return {
    min_change_pct: String(row.fluctuation ?? SHORT_DEFAULTS.min_change_pct),
    min_funding_pct: String(row.f_percent_low ?? SHORT_DEFAULTS.min_funding_pct),
    entry_usdt: String(row.entry_usdt ?? SHORT_DEFAULTS.entry_usdt),
    leverage_info: String(row.leverage_info ?? SHORT_DEFAULTS.leverage_info),
    benefit_ratio: String(row.benefit_ratio ?? SHORT_DEFAULTS.benefit_ratio),
    benefit_ratio_after_grid: String(
      row.benefit_ratio_after_grid ?? SHORT_DEFAULTS.benefit_ratio_after_grid
    ),
    max_symbols: String(row.auto_count ?? SHORT_DEFAULTS.max_symbols),
    ha_break_short: String(row.ha_break_short ?? SHORT_DEFAULTS.ha_break_short),
    ha_break_tf_short: String(row.ha_break_tf_short ?? SHORT_DEFAULTS.ha_break_tf_short),
    ha_break_bars_short: String(
      row.ha_break_bars_short ?? SHORT_DEFAULTS.ha_break_bars_short
    ),
    crypto_futures_only: String(
      row.crypto_futures_only ?? SHORT_DEFAULTS.crypto_futures_only
    ),
    short_trade_mode:
      String(row.short_trade_mode ?? SHORT_DEFAULTS.short_trade_mode).toLowerCase() ===
      "semi"
        ? "semi"
        : "auto",
  };
}

function longFormFromRow(row) {
  return {
    min_change_pct_long: String(row.fluctuation_long ?? LONG_DEFAULTS.min_change_pct_long),
    max_funding_pct_long: String(row.f_percent_long ?? LONG_DEFAULTS.max_funding_pct_long),
    entry_usdt_long: String(row.entry_usdt_long ?? LONG_DEFAULTS.entry_usdt_long),
    leverage_info_long: String(row.leverage_info_long ?? LONG_DEFAULTS.leverage_info_long),
    benefit_ratio_long: String(row.benefit_ratio_long ?? LONG_DEFAULTS.benefit_ratio_long),
    benefit_ratio_after_grid_long: String(
      row.benefit_ratio_after_grid_long ?? LONG_DEFAULTS.benefit_ratio_after_grid_long
    ),
    max_symbols_long: String(row.auto_count_long ?? LONG_DEFAULTS.max_symbols_long),
    ha_tf_long: String(row.ha_tf_long ?? LONG_DEFAULTS.ha_tf_long),
    crypto_futures_only: String(
      row.crypto_futures_only ?? SHORT_DEFAULTS.crypto_futures_only
    ),
  };
}

function shortGridsFromRow(row) {
  return Array.from({ length: GRID_LEVELS }, (_, i) => {
    const n = i + 1;
    return {
      usdt: String(row[`grid_usdt_${n}`] ?? "0"),
      rate: String(row[`grid_rate_${n}`] ?? "0"),
    };
  });
}

function longGridsFromRow(row) {
  return Array.from({ length: GRID_LEVELS }, (_, i) => {
    const n = i + 1;
    return {
      usdt: String(row[`grid_usdt_${n}_long`] ?? "0"),
      rate: String(row[`grid_rate_${n}_long`] ?? "0"),
    };
  });
}

function shortSymbol(sym) {
  if (!sym) return "";
  return String(sym).split("/")[0] || sym;
}

function formatFundingPct(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(4)}%`;
}

function formatChangePct(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

function MemeFundingRankTable({
  title,
  rows,
  tone,
  overlapSymbols,
  selectedSymbol,
  onSymbolClick,
}) {
  return (
    <div className={`meme-funding-panel meme-funding-panel--${tone}`}>
      <h3 className="meme-funding-panel__title">{title}</h3>
      {!rows.length ? (
        <p className="meme-symbols-empty">데이터 없음</p>
      ) : (
        <div className="meme-funding-table-wrap">
          <table className="meme-funding-table">
            <thead>
              <tr>
                <th>#</th>
                <th>심볼</th>
                <th>펀딩비</th>
                <th>등락</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const overlapped = overlapSymbols?.has(row.symbol);
                const selected = selectedSymbol === row.symbol;
                return (
                  <tr
                    key={`${tone}-${row.symbol}`}
                    className={[
                      overlapped ? "is-overlap" : "",
                      selected ? "is-selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <td>{row.rank}</td>
                    <td title={row.symbol}>
                      <button
                        type="button"
                        className="meme-symbol-link"
                        onClick={() => onSymbolClick?.(row)}
                      >
                        {row.base || shortSymbol(row.symbol)}
                      </button>
                    </td>
                    <td className="meme-funding-table__funding">
                      {formatFundingPct(row.funding_pct)}
                    </td>
                    <td>{formatChangePct(row.change_pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemeSettingsForm({
  side,
  form,
  grids,
  onChange,
  onGridChange,
  onCheckboxChange,
  fundingLabel,
  maxSymbolsLabel = "동시 심볼 수",
  afterEntrySlot = null,
  isSemi = false,
}) {
  const isLong = side === "long";
  const changeKey = isLong ? "min_change_pct_long" : "min_change_pct";
  const fundingKey = isLong ? "max_funding_pct_long" : "min_funding_pct";
  const entryKey = isLong ? "entry_usdt_long" : "entry_usdt";
  const leverageKey = isLong ? "leverage_info_long" : "leverage_info";
  const maxSymbolsKey = isLong ? "max_symbols_long" : "max_symbols";
  const benefitKey = isLong ? "benefit_ratio_long" : "benefit_ratio";
  const benefitAfterKey = isLong ? "benefit_ratio_after_grid_long" : "benefit_ratio_after_grid";
  const hideScanThresholds = isSemi && !isLong;

  return (
    <>
      <section className="meme-card">
        <div className="meme-card__head">
          <span className="meme-card__step">1</span>
          <h2 className="meme-card__title">심볼 스캔 조건 ({isLong ? "롱" : "숏"})</h2>
        </div>
        <div className="meme-field-grid">
          {!hideScanThresholds ? (
            <>
              <label className="meme-field">
                <span className="meme-field__label">등락률 기준 (% 이상)</span>
                <input
                  className="meme-field__input"
                  type="number"
                  step="0.1"
                  min="0"
                  value={form[changeKey]}
                  onChange={onChange(changeKey)}
                />
              </label>
              <label className="meme-field">
                <span className="meme-field__label">{fundingLabel}</span>
                <input
                  className="meme-field__input"
                  type="number"
                  step="0.0001"
                  value={form[fundingKey]}
                  onChange={onChange(fundingKey)}
                />
              </label>
            </>
          ) : null}
          {isLong ? (
            <label className="meme-field">
              <span className="meme-field__label">보조봉</span>
              <select
                className="meme-field__input"
                value={form.ha_tf_long ?? ""}
                onChange={onChange("ha_tf_long")}
              >
                {HA_TF_LONG_OPTIONS.map((opt) => (
                  <option key={opt.value || "off"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="meme-field-grid meme-field-grid--2 meme-field--full">
              <label className="meme-field">
                <span className="meme-field__label">HA 최고가 돌파</span>
                <select
                  className="meme-field__input"
                  value={form.ha_break_short ?? "0"}
                  onChange={onChange("ha_break_short")}
                >
                  <option value="0">미적용</option>
                  <option value="1">적용</option>
                </select>
              </label>
              <label className="meme-field meme-field--checkbox-cell">
                <span className="meme-field__label meme-field__label--spacer">
                  &nbsp;
                </span>
                <span className="meme-checkbox-row">
                  <input
                    type="checkbox"
                    checked={form.crypto_futures_only === "1"}
                    onChange={onCheckboxChange("crypto_futures_only")}
                  />
                  <span>Futures만</span>
                </span>
              </label>
            </div>
          )}
        </div>
        {!isLong && form.ha_break_short === "1" ? (
          <div className="meme-field-grid meme-field-grid--2" style={{ marginTop: 14 }}>
            <label className="meme-field">
              <span className="meme-field__label">돌파 시간봉</span>
              <select
                className="meme-field__input"
                value={form.ha_break_tf_short ?? "1h"}
                onChange={onChange("ha_break_tf_short")}
              >
                {HA_BREAK_TF_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="meme-field">
              <span className="meme-field__label">과거 봉 수 (현재봉 제외)</span>
              <input
                className="meme-field__input"
                type="number"
                step="1"
                min="2"
                max="500"
                value={form.ha_break_bars_short ?? "20"}
                onChange={onChange("ha_break_bars_short")}
              />
            </label>
          </div>
        ) : null}
        {isLong ? (
          <label className="meme-field meme-field--checkbox-simple">
            <input
              type="checkbox"
              checked={form.crypto_futures_only === "1"}
              onChange={onCheckboxChange("crypto_futures_only")}
            />
            <span className="meme-field__label">Futures만</span>
          </label>
        ) : null}
        {/* <p className="meme-card__hint" style={{ marginTop: 12 }}>
          {isLong ? (
            <>
              1D 하이킨아시 <strong>양봉</strong>
              {form.ha_tf_long ? (
                <>
                  {" "}
                  + 보조봉(<strong>{form.ha_tf_long}</strong>) <strong>양봉</strong>
                </>
              ) : null}
            </>
          ) : (
            <>1D 하이킨아시 <strong>양봉</strong> 조건</>
          )}
        </p> */}
      </section>

      <section className="meme-card">
        <div className="meme-card__head">
          <span className="meme-card__step">2</span>
          <h2 className="meme-card__title">진입 설정 ({isLong ? "롱" : "숏"})</h2>
        </div>
        <div className="meme-field-grid meme-field-grid--3">
          <label className="meme-field">
            <span className="meme-field__label">진입 USDT</span>
            <input
              className="meme-field__input"
              type="number"
              step="1"
              min="1"
              value={form[entryKey]}
              onChange={onChange(entryKey)}
            />
          </label>
          <label className="meme-field">
            <span className="meme-field__label">레버리지</span>
            <input
              className="meme-field__input"
              type="number"
              step="1"
              min="1"
              value={form[leverageKey]}
              onChange={onChange(leverageKey)}
            />
          </label>
          <label className="meme-field">
            <span className="meme-field__label">{maxSymbolsLabel}</span>
            <input
              className="meme-field__input"
              type="number"
              step="1"
              min="1"
              max="100"
              value={form[maxSymbolsKey]}
              onChange={onChange(maxSymbolsKey)}
            />
          </label>
        </div>
      </section>

      {afterEntrySlot}

      <section className="meme-card">
        <div className="meme-card__head">
          <span className="meme-card__step">3</span>
          <h2 className="meme-card__title">그리드 지정가 10단 ({isLong ? "롱" : "숏"})</h2>
        </div>
        <p className="meme-card__hint" style={{ marginBottom: 12 }}>
          시장가 첫 진입 후, 진입가 기준 불리한 방향으로 rate(%) 간격을 누적해 지정가를 배치합니다.
        </p>
        <div className="meme-grid-table-wrap">
          <table className="meme-grid-table">
            <thead>
              <tr>
                <th>단계</th>
                <th>USDT</th>
                <th>간격 rate (%)</th>
              </tr>
            </thead>
            <tbody>
              {grids.map((row, i) => (
                <tr key={i}>
                  <td className="meme-grid-table__step">{i + 1}</td>
                  <td>
                    <input
                      className="meme-field__input meme-grid-table__input"
                      type="number"
                      step="1"
                      min="0"
                      value={row.usdt}
                      onChange={onGridChange(i, "usdt")}
                    />
                  </td>
                  <td>
                    <input
                      className="meme-field__input meme-grid-table__input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={row.rate}
                      onChange={onGridChange(i, "rate")}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="meme-card">
        <div className="meme-card__head">
          <span className="meme-card__step">4</span>
          <h2 className="meme-card__title">익절 ({isLong ? "롱" : "숏"})</h2>
        </div>
        <div className="meme-field-grid">
          <label className="meme-field">
            <span className="meme-field__label">목표 가격변동 (%)</span>
            <input
              className="meme-field__input"
              type="number"
              step="0.1"
              min="0.1"
              value={form[benefitKey]}
              onChange={onChange(benefitKey)}
            />
          </label>
          <label className="meme-field">
            <span className="meme-field__label">그리드 소진 후 목표 가격변동 (%)</span>
            <input
              className="meme-field__input"
              type="number"
              step="0.1"
              min="0.1"
              value={form[benefitAfterKey]}
              onChange={onChange(benefitAfterKey)}
            />
          </label>
        </div>
      </section>
    </>
  );
}

export default function SetPageMeme() {
  const [cookies] = useCookies(["uuid"]);
  const [uid, setUID] = useState(0);
  const [nickname, setNickname] = useState("");
  const [activeTab, setActiveTab] = useState("auto");
  const [autoTradeShort, setAutoTradeShort] = useState(false);
  const [shortTradeMode, setShortTradeMode] = useState("auto");
  const [activeSymbols, setActiveSymbols] = useState([]);
  const [shortForm, setShortForm] = useState(SHORT_DEFAULTS);
  const [shortGrids, setShortGrids] = useState(EMPTY_GRIDS);
  const [loading, setLoading] = useState(false);
  const [shortPresets, setShortPresets] = useState([]);
  const [selectedShortPresetId, setSelectedShortPresetId] = useState("");
  const [fundingHighest, setFundingHighest] = useState([]);
  const [fundingLowest, setFundingLowest] = useState([]);
  const [fundingListLoading, setFundingListLoading] = useState(false);
  const [fundingListError, setFundingListError] = useState("");
  const [fundingListTotal, setFundingListTotal] = useState(0);
  const [fundingRefreshKey, setFundingRefreshKey] = useState(0);
  const [watchMinChange, setWatchMinChange] = useState("5");
  const [watchActive, setWatchActive] = useState(false);
  const [watchItems, setWatchItems] = useState([]);
  const [watchMeta, setWatchMeta] = useState(null);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState("");
  const [watchScanning, setWatchScanning] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState("");

  const refreshPresets = useCallback((targetUid) => {
    setShortPresets(readMemePresets(targetUid, "short"));
  }, []);

  const loadSideSettings = useCallback((targetUid) => {
    return fetch(`/setdata_first_meme/${targetUid}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const row = rows[0];
        if (!row) return;
        const form = shortFormFromRow(row);
        setShortForm(form);
        setShortGrids(shortGridsFromRow(row));
        setShortTradeMode(form.short_trade_mode);
      })
      .catch(() => {});
  }, []);

  const loadSettings = useCallback((targetUid) => {
    fetch(`/setdata_first_meme/${targetUid}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const row = rows[0];
        if (!row) return;
        const running =
          row.auto_trade_short === 1
          || row.auto_trade_short === "1"
          || row.auto_trade === 1
          || row.auto_trade === "1";
        setAutoTradeShort(running);
        const form = shortFormFromRow(row);
        setShortForm(form);
        setShortGrids(shortGridsFromRow(row));
        setShortTradeMode(form.short_trade_mode);
        if (running) {
          setActiveTab(form.short_trade_mode === "semi" ? "semi" : "auto");
        }
      })
      .catch(() => {});

    fetch(`/startcheck_meme/list_entry?uid=${targetUid}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setActiveSymbols(data))
      .catch(() => setActiveSymbols([]));
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("meme-scroll-root");
    return () => {
      document.documentElement.classList.remove("meme-scroll-root");
    };
  }, []);

  useEffect(() => {
    if (!cookies.uuid) {
      alert("로그인 정보가 존재하지 않습니다.");
      window.location.href = "/LoginAts";
      return;
    }

    fetch("/memeversion_selected", { credentials: "same-origin" })
      .then((r) => {
        if (!r.ok) throw new Error("memeversion_selected failed");
        return r.json();
      })
      .then((data) => {
        if (data.message !== "ok") {
          alert(data.message);
          window.location.href = data.is_admin_access ? "/Adminpage" : "/SelectPage";
          return null;
        }
        return fetch("/GetUserInfo", { credentials: "same-origin" }).then((r) => {
          if (!r.ok) throw new Error("GetUserInfo failed");
          return r.json();
        });
      })
      .then((user) => {
        if (!user) return;
        setUID(user.uid);
        setNickname(user.nickname);
        loadSettings(user.uid);
        refreshPresets(user.uid);
      })
      .catch(() => {
        const adminCookie = document.cookie
          .split(";")
          .map((c) => c.trim())
          .find((c) => c.startsWith("admin_target_uid="));
        if (adminCookie) {
          alert("관리자 접근 오류가 발생했습니다. 어드민 페이지로 이동합니다.");
          window.location.href = "/Adminpage";
        } else {
          alert("로그인 정보가 존재하지 않습니다.");
          window.location.href = "/LoginAts";
        }
      });
  }, [cookies.uuid, loadSettings, refreshPresets]);

  const isSemiTab = activeTab === "semi";

  useEffect(() => {
    if (!isSemiTab || !uid) {
      return undefined;
    }
    const limitRaw = parseInt(shortForm.max_symbols, 10);
    const limit = Number.isFinite(limitRaw)
      ? Math.min(Math.max(limitRaw, 1), 100)
      : 10;
    const futuresOnly = shortForm.crypto_futures_only === "1" ? "1" : "0";
    let cancelled = false;
    const timer = setTimeout(() => {
      setFundingListLoading(true);
      setFundingListError("");
      fetch(
        `/meme_semi_funding_list?uid=${uid}&limit=${limit}&crypto_futures_only=${futuresOnly}`
      )
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) {
            throw new Error(data.message || "펀딩 리스트 조회 실패");
          }
          return data;
        })
        .then((data) => {
          if (cancelled) return;
          setFundingHighest(Array.isArray(data.highest) ? data.highest : []);
          setFundingLowest(Array.isArray(data.lowest) ? data.lowest : []);
          setFundingListTotal(Number(data.total) || 0);
        })
        .catch((err) => {
          if (cancelled) return;
          setFundingHighest([]);
          setFundingLowest([]);
          setFundingListTotal(0);
          setFundingListError(err.message || "펀딩 리스트 조회 실패");
        })
        .finally(() => {
          if (!cancelled) setFundingListLoading(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    isSemiTab,
    uid,
    shortForm.max_symbols,
    shortForm.crypto_futures_only,
    fundingRefreshKey,
  ]);

  const applyWatchPayload = useCallback((data) => {
    setWatchActive(Boolean(data.active));
    setWatchItems(Array.isArray(data.items) ? data.items : []);
    setWatchMeta({
      min_change_pct: data.min_change_pct,
      crypto_futures_only: data.crypto_futures_only,
      started_at: data.started_at,
      added_count: data.added_count,
      scanned: data.scanned,
    });
    if (data.min_change_pct != null && data.min_change_pct !== "") {
      setWatchMinChange(String(data.min_change_pct));
    }
  }, []);

  const loadWatchStatus = useCallback(() => {
    if (!uid) return Promise.resolve();
    return fetch(`/meme_change_watch/status?uid=${uid}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || "감시 상태 조회 실패");
        return data;
      })
      .then((data) => {
        applyWatchPayload(data);
        setWatchError("");
      })
      .catch((err) => {
        setWatchError(err.message || "감시 상태 조회 실패");
      });
  }, [uid, applyWatchPayload]);

  const runWatchScan = useCallback(async () => {
    if (!uid) return;
    setWatchScanning(true);
    try {
      const r = await fetch("/meme_change_watch/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "등락률 스캔 실패");
      applyWatchPayload(data);
      setWatchError("");
    } catch (err) {
      setWatchError(err.message || "등락률 스캔 실패");
    } finally {
      setWatchScanning(false);
    }
  }, [uid, applyWatchPayload]);

  useEffect(() => {
    if (!uid || !isSemiTab) return undefined;
    let cancelled = false;
    setWatchLoading(true);
    loadWatchStatus().finally(() => {
      if (!cancelled) setWatchLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [uid, isSemiTab, loadWatchStatus]);

  // 서버가 45초마다 스캔 — 화면은 저장된 리스트만 주기적으로 갱신
  useEffect(() => {
    if (!uid || !isSemiTab || !watchActive) return undefined;
    loadWatchStatus();
    const timer = setInterval(() => {
      loadWatchStatus();
    }, 15000);
    return () => clearInterval(timer);
  }, [uid, isSemiTab, watchActive, loadWatchStatus]);

  const onWatchStart = async () => {
    if (!uid || watchLoading) return;
    const minRaw = parseFloat(watchMinChange);
    const minChange = Number.isFinite(minRaw) ? minRaw : 5;
    setWatchLoading(true);
    setWatchError("");
    try {
      const r = await fetch("/meme_change_watch/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          min_change_pct: minChange,
          crypto_futures_only: shortForm.crypto_futures_only === "1" ? 1 : 0,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "감시 시작 실패");
      applyWatchPayload(data);
      setWatchActive(true);
      setWatchMinChange(String(data.min_change_pct ?? minChange));
    } catch (err) {
      setWatchError(err.message || "감시 시작 실패");
    } finally {
      setWatchLoading(false);
    }
  };

  const onWatchStop = async () => {
    if (!uid || watchLoading) return;
    setWatchLoading(true);
    setWatchError("");
    try {
      const r = await fetch("/meme_change_watch/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "감시 정지 실패");
      setWatchActive(false);
    } catch (err) {
      setWatchError(err.message || "감시 정지 실패");
    } finally {
      setWatchLoading(false);
    }
  };

  const onWatchDelete = async (symbol) => {
    if (!uid || !symbol) return;
    try {
      const r = await fetch("/meme_change_watch/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, symbol }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "삭제 실패");
      setWatchItems(Array.isArray(data.items) ? data.items : []);
      setWatchError("");
    } catch (err) {
      setWatchError(err.message || "삭제 실패");
    }
  };

  const onWatchClear = async () => {
    if (!uid) return;
    if (!window.confirm("누적된 등락률 리스트를 모두 비울까요?")) return;
    try {
      const r = await fetch("/meme_change_watch/clear", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "초기화 실패");
      setWatchItems([]);
      setWatchError("");
    } catch (err) {
      setWatchError(err.message || "초기화 실패");
    }
  };

  const isRunningThisMode = autoTradeShort && shortTradeMode === activeTab;

  const isSelectedSymbolActive = useMemo(() => {
    if (!selectedSymbol) return false;
    return activeSymbols.some(
      (item) =>
        item.direction !== "long"
        && (item.symbol || item) === selectedSymbol
    );
  }, [selectedSymbol, activeSymbols]);
  const tabPresets = shortPresets;
  const selectedPresetId = selectedShortPresetId;
  const setSelectedPresetId = setSelectedShortPresetId;

  const fundingSymbolSet = new Set([
    ...fundingHighest.map((r) => r.symbol),
    ...fundingLowest.map((r) => r.symbol),
  ]);
  const watchSymbolSet = new Set(watchItems.map((r) => r.symbol));
  const overlapSymbols = new Set(
    [...watchSymbolSet].filter((sym) => fundingSymbolSet.has(sym))
  );

  const onSymbolClick = (row) => {
    if (!row?.symbol) return;
    setSelectedSymbol((prev) => (prev === row.symbol ? "" : row.symbol));
  };

  const switchTab = (mode) => {
    setActiveTab(mode);
    setSelectedShortPresetId("");
    if (uid) loadSideSettings(uid);
  };

  const onPresetChange = (e) => {
    const presetId = e.target.value;
    setSelectedPresetId(presetId);
    if (!presetId) {
      if (uid) loadSideSettings(uid);
      return;
    }
    const preset = tabPresets.find((p) => p.id === presetId);
    if (!preset?.snapshot) return;
    applyMemePresetSnapshot(preset.snapshot, SHORT_DEFAULTS, setShortForm, setShortGrids);
  };

  const onSavePreset = () => {
    if (!uid) return;
    const list = readMemePresets(uid, "short");
    const name = window.prompt("프리셋 이름을 입력하세요.", `프리셋 ${list.length + 1}`);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      alert("프리셋 이름을 입력해 주세요.");
      return;
    }
    if (list.length >= MEME_PRESET_MAX) {
      alert(`프리셋은 최대 ${MEME_PRESET_MAX}개까지 저장할 수 있습니다.`);
      return;
    }
    const snapshot = buildMemePresetSnapshot(shortForm, shortGrids);
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const next = [...list, { id, name: trimmed, snapshot }];
    writeMemePresets(uid, "short", next);
    refreshPresets(uid);
    setSelectedPresetId(id);
    alert(`"${trimmed}" 숏 프리셋을 저장했습니다.`);
  };

  const onDeletePreset = () => {
    if (!uid || !selectedPresetId) {
      alert("삭제할 프리셋을 선택하세요.");
      return;
    }
    const target = tabPresets.find((p) => p.id === selectedPresetId);
    if (!target) return;
    if (!window.confirm(`"${target.name}" 숏 프리셋을 삭제할까요?`)) return;
    const next = tabPresets.filter((p) => p.id !== selectedPresetId);
    writeMemePresets(uid, "short", next);
    refreshPresets(uid);
    setSelectedPresetId("");
    loadSideSettings(uid);
  };

  const makeOnChange = (setter) => (key) => (e) => {
    setter((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const makeOnCheckboxChange = (setter) => (key) => (e) => {
    setter((prev) => ({ ...prev, [key]: e.target.checked ? "1" : "0" }));
  };

  const makeOnGridChange = (setter) => (index, key) => (e) => {
    const value = e.target.value;
    setter((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const buildBody = () => {
    const body = {
      uid,
      ...shortForm,
      short_trade_mode: activeTab === "semi" ? "semi" : "auto",
    };
    shortGrids.forEach((row, i) => {
      const n = i + 1;
      body[`grid_usdt_${n}`] = row.usdt;
      body[`grid_rate_${n}`] = row.rate;
    });
    return body;
  };

  const onStart = () => {
    if (!uid || loading) return;
    if (isSemiTab) {
      if (!selectedSymbol) {
        alert("리스트에서 코인을 먼저 선택하세요.");
        return;
      }
      setLoading(true);
      fetch("/start_meme_semi_symbol", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...buildBody(),
          symbol: selectedSymbol,
        }),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          alert(data.message || (ok ? "진입 요청 완료" : "진입 요청 실패"));
          if (!ok) return;
          setAutoTradeShort(true);
          setShortTradeMode("semi");
          setSelectedShortPresetId("");
          loadSettings(uid);
        })
        .catch(() => alert("매매시작 요청 중 오류가 발생했습니다."))
        .finally(() => setLoading(false));
      return;
    }

    setLoading(true);
    fetch("/start_meme_short", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildBody()),
    })
      .then((r) => r.json())
      .then((res) => {
        alert(res.message || "시작 요청 완료");
        setAutoTradeShort(true);
        setShortTradeMode("auto");
        setSelectedShortPresetId("");
        loadSettings(uid);
      })
      .catch(() => alert("시작 요청 중 오류가 발생했습니다."))
      .finally(() => setLoading(false));
  };

  const onStop = () => {
    if (!uid || loading) return;
    if (isSemiTab) {
      if (!selectedSymbol) {
        alert("리스트에서 코인을 먼저 선택하세요.");
        return;
      }
      if (!isSelectedSymbolActive) {
        alert("진입 중인 코인만 매매정지할 수 있습니다.");
        return;
      }
      const coin = shortSymbol(selectedSymbol);
      if (!window.confirm(`${coin} 숏 매매를 정지하시겠습니까?\n(포지션 청산 + 그리드 취소)`)) return;
      setLoading(true);
      fetch("/stop_meme_semi_symbol", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, symbol: selectedSymbol }),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          alert(data.message || (ok ? "정지 요청 완료" : "정지 요청 실패"));
          if (!ok) return;
          loadSettings(uid);
        })
        .catch(() => alert("매매정지 요청 중 오류가 발생했습니다."))
        .finally(() => setLoading(false));
      return;
    }

    if (!window.confirm("Meme 숏 매매를 정지하시겠습니까?")) return;
    setLoading(true);
    fetch("/stop_meme_short", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uid }),
    })
      .then((r) => r.json())
      .then((res) => {
        alert(res.message || "정지되었습니다.");
        setAutoTradeShort(false);
      })
      .catch(() => alert("정지 요청 중 오류가 발생했습니다."))
      .finally(() => setLoading(false));
  };

    return (
    <div className="meme-page">
      <div className="meme-blur meme-blur-1" />
      <div className="meme-blur meme-blur-2" />

      <div className="meme-container">
        <header className="meme-hero">
          <div className="meme-hero__top">
            <div>
              <span className="meme-hero__badge">🪙 MEME SCAN</span>
              <h1 className="meme-hero__title">숏 스캔 매매</h1>
              <p className="meme-hero__desc">
                자동은 기존처럼 스캔·진입하고, 반자동은 기능을 하나씩 추가합니다.
              </p>
                </div>
            <div className="meme-hero__user">
              <span
                className={`meme-status-pill ${autoTradeShort && shortTradeMode === "auto" ? "meme-status-pill--on" : "meme-status-pill--off"}`}
              >
                <span className="meme-status-dot" />
                자동 {autoTradeShort && shortTradeMode === "auto" ? "실행" : "정지"}
              </span>
              <span
                className={`meme-status-pill ${autoTradeShort && shortTradeMode === "semi" ? "meme-status-pill--on" : "meme-status-pill--off"}`}
                style={{ marginLeft: 8 }}
              >
                <span className="meme-status-dot" />
                반자동 {autoTradeShort && shortTradeMode === "semi" ? "실행" : "정지"}
              </span>
              <span className="meme-hero__nickname">{nickname || "로딩 중..."}</span>
              <span className="meme-hero__uid">UID {uid || "—"}</span>
                </div>
                </div>
        </header>

        <div className="meme-layout">
          <div className="meme-main">
            <div className="meme-direction-toggle" style={{ marginBottom: 16 }}>
              <button
                type="button"
                className={`meme-direction-btn meme-direction-btn--short ${!isSemiTab ? "is-active" : ""}`}
                onClick={() => switchTab("auto")}
              >
                자동
              </button>
              <button
                type="button"
                className={`meme-direction-btn meme-direction-btn--long ${isSemiTab ? "is-active" : ""}`}
                onClick={() => switchTab("semi")}
              >
                반자동
              </button>
                </div>

            {isSemiTab ? (
              <section className="meme-card" style={{ marginBottom: 14 }}>
                <div className="meme-card__head">
                  <span className="meme-card__step">半</span>
                  <h2 className="meme-card__title">반자동 모드</h2>
                </div>
                <p className="meme-card__hint" style={{ margin: 0 }}>
                  오른쪽에서 등락률을 입력하고 감시를 시작하면, 기준 이상 코인이
                  하나씩 누적됩니다. Futures만 체크 시 크립토 Futures만 감시합니다.
                </p>
              </section>
            ) : null}

              <MemeSettingsForm
                side="short"
                form={shortForm}
                grids={shortGrids}
                onChange={makeOnChange(setShortForm)}
                onGridChange={makeOnGridChange(setShortGrids)}
                onCheckboxChange={makeOnCheckboxChange(setShortForm)}
                fundingLabel="펀딩비 기준 (% 이상)"
                maxSymbolsLabel={isSemiTab ? "리스트 갯수" : "동시 심볼 수"}
                isSemi={isSemiTab}
                afterEntrySlot={
                  isSemiTab ? (
                    <section className="meme-card">
                      <div className="meme-card__head">
                        <span className="meme-card__step">L</span>
                        <h2 className="meme-card__title">펀딩비 순위 리스트</h2>
                      </div>
                      <div className="meme-funding-toolbar">
                        <p className="meme-card__hint" style={{ margin: 0 }}>
                          {shortForm.crypto_futures_only === "1"
                            ? "Futures(크립토)만"
                            : "전체 스왑(주식·RWA 포함)"}
                          {fundingListTotal
                            ? ` · 후보 ${fundingListTotal}개 중 상위/하위 각 ${
                                Math.min(
                                  Math.max(parseInt(shortForm.max_symbols, 10) || 10, 1),
                                  100
                                )
                              }개`
                            : null}
                        </p>
                        <button
                          type="button"
                          className="meme-btn meme-btn--preset"
                          disabled={fundingListLoading || !uid}
                          onClick={() => setFundingRefreshKey((k) => k + 1)}
                        >
                          {fundingListLoading ? "불러오는 중..." : "새로고침"}
                        </button>
                      </div>
                      {fundingListError ? (
                        <p className="meme-funding-error">{fundingListError}</p>
                      ) : null}
                      {overlapSymbols.size > 0 ? (
                        <p className="meme-card__hint" style={{ margin: "0 0 10px" }}>
                          등락률 리스트와 중복 {overlapSymbols.size}개 · 연한 박스로 표시
                          {selectedSymbol
                            ? ` · 선택: ${shortSymbol(selectedSymbol)}`
                            : ""}
                        </p>
                      ) : selectedSymbol ? (
                        <p className="meme-card__hint" style={{ margin: "0 0 10px" }}>
                          선택: {shortSymbol(selectedSymbol)}
                        </p>
                      ) : null}
                      <div className="meme-funding-grid">
                        <MemeFundingRankTable
                          title="펀딩비 높은 순"
                          rows={fundingHighest}
                          tone="high"
                          overlapSymbols={overlapSymbols}
                          selectedSymbol={selectedSymbol}
                          onSymbolClick={onSymbolClick}
                        />
                        <MemeFundingRankTable
                          title="펀딩비 낮은 순"
                          rows={fundingLowest}
                          tone="low"
                          overlapSymbols={overlapSymbols}
                          selectedSymbol={selectedSymbol}
                          onSymbolClick={onSymbolClick}
                        />
                      </div>
                    </section>
                  ) : null
                }
              />

            <section className="meme-card meme-card--preset">
              <div className="meme-card__head">
                <span className="meme-card__step">★</span>
                <h2 className="meme-card__title">숏 프리셋</h2>
              </div>
              <p className="meme-card__hint" style={{ marginBottom: 12 }}>
                자동/반자동이 같은 숏 설정을 공유합니다. 시작 시 현재 탭 모드로 저장·실행됩니다.
              </p>
              <div className="meme-preset-row">
                <select
                  className="meme-field__input meme-preset-select"
                  value={selectedPresetId}
                  onChange={onPresetChange}
                >
                  <option value="">서버 저장값</option>
                  {tabPresets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                  </select>
                <button type="button" className="meme-btn meme-btn--preset" onClick={onSavePreset}>
                  현재 저장
                </button>
                <button type="button" className="meme-btn meme-btn--preset meme-btn--preset-del" onClick={onDeletePreset}>
                  삭제
                </button>
            </div>
            </section>

            <div className="meme-actions">
              <button
                type="button"
                className="meme-btn meme-btn--start"
                disabled={
                  loading
                  || (isSemiTab
                    ? !selectedSymbol || isSelectedSymbolActive
                    : isRunningThisMode)
                }
                onClick={onStart}
              >
                {loading
                  ? "처리 중..."
                  : isSemiTab
                    ? selectedSymbol
                      ? `▶ ${shortSymbol(selectedSymbol)} 매매시작`
                      : "▶ 코인을 선택하세요"
                    : "▶ 자동매매 시작"}
                </button>
              <button
                type="button"
                className="meme-btn meme-btn--stop"
                disabled={
                  loading
                  || (isSemiTab
                    ? !selectedSymbol || !isSelectedSymbolActive
                    : !autoTradeShort)
                }
                onClick={onStop}
              >
                {isSemiTab
                  ? selectedSymbol
                    ? `■ ${shortSymbol(selectedSymbol)} 매매정지`
                    : "■ 코인을 선택하세요"
                  : "■ 정지"}
              </button>
            </div>
          </div>

          <aside className="meme-side">
            <div className="meme-symbols-card">
              <h3>
                숏 진입 중
                <span className="meme-symbols-count">
                  {activeSymbols.filter((item) => item.direction !== "long").length}개
                </span>
              </h3>
              {activeSymbols.filter((item) => item.direction !== "long").length === 0 ? (
                <p className="meme-symbols-empty">숏 포지션 없음</p>
              ) : (
                <div className="meme-symbol-chips">
                  {activeSymbols
                    .filter((item) => item.direction !== "long")
                    .map((item) => {
                      const sym = item.symbol || item;
                      const key = `${sym}:${item.direction || "short"}`;
                      return (
                        <span key={key} className="meme-symbol-chip" title={sym}>
                          {shortSymbol(sym)}
                        </span>
                      );
                    })}
                </div>
              )}
            </div>

            {isSemiTab ? (
            <div className="meme-symbols-card" style={{ marginTop: 16 }}>
              <h3>
                등락률 기준 (% 이상)
                <span className="meme-symbols-count">{watchItems.length}개</span>
              </h3>
              <div className="meme-change-side-toolbar">
                <input
                  type="number"
                  className="meme-field__input meme-change-date-select"
                  step="0.1"
                  min="0"
                  value={watchMinChange}
                  onChange={(e) => setWatchMinChange(e.target.value)}
                  disabled={watchActive || watchLoading}
                  title="등락률 기준 %"
                  placeholder="등락률 %"
                />
                {watchActive ? (
                  <button
                    type="button"
                    className="meme-btn meme-btn--preset meme-btn--preset-del"
                    disabled={watchLoading || !uid}
                    onClick={onWatchStop}
                  >
                    감시 정지
                  </button>
                ) : (
                  <button
                    type="button"
                    className="meme-btn meme-btn--preset"
                    disabled={watchLoading || !uid}
                    onClick={onWatchStart}
                  >
                    {watchLoading ? "..." : "등락률 감시 시작"}
                  </button>
                )}
              </div>
              <div className="meme-change-side-toolbar" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="meme-btn meme-btn--preset"
                  disabled={!watchActive || watchScanning || !uid}
                  onClick={runWatchScan}
                >
                  {watchScanning ? "스캔 중..." : "지금 스캔"}
                </button>
                <button
                  type="button"
                  className="meme-btn meme-btn--preset meme-btn--preset-del"
                  disabled={!watchItems.length || !uid}
                  onClick={onWatchClear}
                >
                  전체 삭제
                </button>
              </div>
              <p className="meme-card__hint" style={{ margin: "8px 0 10px", fontSize: 12 }}>
                {watchActive ? "서버 감시 중 (45초마다 스캔)" : "감시 정지"}
                {watchMeta?.min_change_pct != null
                  ? ` · 기준 ${watchMeta.min_change_pct}%↑`
                  : ` · 기준 ${watchMinChange}%↑`}
                {Number(watchMeta?.crypto_futures_only) === 1 ||
                shortForm.crypto_futures_only === "1"
                  ? " · Futures만"
                  : null}
                {watchScanning ? " · 스캔 중" : null}
              </p>
              {watchError ? (
                <p className="meme-funding-error">{watchError}</p>
              ) : null}
              {watchLoading && !watchItems.length ? (
                <p className="meme-symbols-empty">불러오는 중...</p>
              ) : !watchItems.length ? (
                <p className="meme-symbols-empty">
                  누적된 코인이 없습니다.
                  <br />
                  <span style={{ opacity: 0.8 }}>
                    등락률을 입력하고 「등락률 감시 시작」을 눌러주세요.
                  </span>
                </p>
              ) : (
                <div className="meme-change-side-list">
                  {watchItems.map((row) => {
                    const overlapped = overlapSymbols.has(row.symbol);
                    const selected = selectedSymbol === row.symbol;
                    return (
                      <div
                        key={row.symbol}
                        className={[
                          "meme-change-side-row",
                          "meme-change-side-row--watch",
                          overlapped ? "is-overlap" : "",
                          selected ? "is-selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        title={`${row.symbol} · 등락 ${formatChangePct(row.change_pct)} · 펀딩 ${formatFundingPct(row.funding_pct)}`}
                      >
                        <span className="meme-change-side-rank">{row.rank}</span>
                        <button
                          type="button"
                          className="meme-symbol-link meme-change-side-sym"
                          onClick={() => onSymbolClick(row)}
                        >
                          {row.base || shortSymbol(row.symbol)}
                        </button>
                        <span className="meme-change-side-pct">
                          {formatChangePct(row.change_pct)}
                        </span>
                        <span className="meme-change-side-funding">
                          {formatFundingPct(row.funding_pct)}
                        </span>
                        <button
                          type="button"
                          className="meme-change-del-btn"
                          onClick={() => onWatchDelete(row.symbol)}
                        >
                          삭제
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            ) : null}
          </aside>
            </div>
          </div>
        </div>
    );
}
